import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/StatusPill";
import { StarRating } from "@/components/StarRating";
import LeadDetailPanel from "@/components/LeadDetailPanel";
import { BulkActionBar } from "@/components/BulkActionBar";
import { STATUS_ORDER, STATUS_LABELS } from "@/lib/templates";
import { RefreshCw, Search, Filter, AlertTriangle, Circle } from "lucide-react";

const ALL = "__all__";
const UNASSIGNED = "__unassigned__";

export default function LeadsBoard() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);

  const [q, setQ] = useState("");
  const [cityFilter, setCityFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [repFilter, setRepFilter] = useState(ALL);
  const [sampleFilter, setSampleFilter] = useState(ALL); // has | pending
  const [selectedIds, setSelectedIds] = useState([]);

  const canBulkAssign = user?.role === "admin" || user?.role === "office_manager";

  const clearSelection = () => setSelectedIds([]);
  const toggleSelected = (placeId) => {
    setSelectedIds((prev) =>
      prev.includes(placeId) ? prev.filter((p) => p !== placeId) : [...prev, placeId]
    );
  };

  const loadLeads = useCallback(async (opts = {}) => {
    if (opts.silent !== true) setLoading(true);
    setError("");
    try {
      const [leadsRes, usersRes] = await Promise.all([
        api.get("/leads"),
        api.get("/users/directory"),
      ]);
      setLeads(leadsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const refreshFeed = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/leads/refresh");
      toast.success(`Feed synced — ${data.inserted} new, ${data.updated} updated`);
      await loadLeads({ silent: true });
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setRefreshing(false);
    }
  };

  const repsById = useMemo(() => {
    const m = {};
    users.forEach((u) => {
      m[u.id] = u;
    });
    return m;
  }, [users]);

  const cities = useMemo(() => {
    const set = new Set();
    leads.forEach((l) => l.city && set.add(l.city));
    return Array.from(set).sort();
  }, [leads]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (query) {
        const hay = `${l.name} ${l.city} ${l.owner || ""} ${l.category || ""} ${l.phone || ""}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      if (cityFilter !== ALL && l.city !== cityFilter) return false;
      if (statusFilter !== ALL && l.status !== statusFilter) return false;
      if (repFilter !== ALL) {
        if (repFilter === UNASSIGNED) {
          if (l.assigned_rep_id) return false;
        } else if (l.assigned_rep_id !== repFilter) return false;
      }
      if (sampleFilter === "has" && !l.sample_url) return false;
      if (sampleFilter === "pending" && l.sample_url) return false;
      return true;
    });
  }, [leads, q, cityFilter, statusFilter, repFilter, sampleFilter]);

  const counts = useMemo(() => {
    const c = { total: leads.length, filtered: filtered.length };
    STATUS_ORDER.forEach((s) => (c[s] = 0));
    leads.forEach((l) => {
      if (c[l.status] != null) c[l.status] += 1;
    });
    return c;
  }, [leads, filtered.length]);

  const onLeadUpdated = (updated) => {
    setLeads((prev) => prev.map((l) => (l.place_id === updated.place_id ? updated : l)));
  };

  const clearFilters = () => {
    setQ("");
    setCityFilter(ALL);
    setStatusFilter(ALL);
    setRepFilter(ALL);
    setSampleFilter(ALL);
  };

  const activeFilterCount =
    (cityFilter !== ALL ? 1 : 0) +
    (statusFilter !== ALL ? 1 : 0) +
    (repFilter !== ALL ? 1 : 0) +
    (sampleFilter !== ALL ? 1 : 0) +
    (q ? 1 : 0);

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="leads-board-page">
      {/* Top bar */}
      <div className="border-b border-line bg-bg-surface px-6 py-4 shrink-0">
        <div className="flex items-baseline justify-between gap-4 mb-3">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
              Leads
            </div>
            <h1 className="text-xl font-semibold tracking-tight text-ink mt-0.5">
              Board
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
              <span className="text-ink tabular-nums" data-testid="leads-count">
                {counts.filtered}
              </span>
              <span className="mx-1">/</span>
              <span className="tabular-nums">{counts.total}</span> shown
            </div>
            <button
              onClick={refreshFeed}
              disabled={refreshing}
              data-testid="refresh-leads-btn"
              className="flex items-center gap-2 text-sm border border-line text-ink hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors disabled:opacity-60"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Syncing…" : "Refresh leads"}
            </button>
          </div>
        </div>

        {/* Status ribbon */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? ALL : s)}
              data-testid={`status-filter-${s}`}
              className={`flex items-center gap-2 px-2.5 py-1 border rounded-sm text-xs transition-colors ${
                statusFilter === s
                  ? "border-cyan bg-cyan-muted text-ink"
                  : "border-line text-ink-secondary hover:bg-bg-hover hover:text-ink"
              }`}
            >
              <Circle size={7} className="fill-current" strokeWidth={0} />
              <span>{STATUS_LABELS[s]}</span>
              <span className="tabular-nums text-ink-muted">{counts[s]}</span>
            </button>
          ))}
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search business, city, owner…"
              data-testid="lead-search-input"
              className="w-full bg-bg border border-line rounded-sm pl-8 pr-3 py-1.5 text-sm text-ink placeholder:text-ink-muted focus:border-cyan transition-colors"
            />
          </div>

          <SelectFilter
            testId="city-filter"
            value={cityFilter}
            onChange={setCityFilter}
            label="City"
            options={[{ value: ALL, label: "All cities" }, ...cities.map((c) => ({ value: c, label: c }))]}
          />

          <SelectFilter
            testId="rep-filter"
            value={repFilter}
            onChange={setRepFilter}
            label="Rep"
            options={[
              { value: ALL, label: "All reps" },
              { value: UNASSIGNED, label: "Unassigned" },
              ...users
                .filter((u) => u.role === "rep")
                .map((u) => ({ value: u.id, label: `${u.name}${!u.active ? " (deactivated)" : ""}` })),
            ]}
          />

          <SelectFilter
            testId="sample-filter"
            value={sampleFilter}
            onChange={setSampleFilter}
            label="Sample"
            options={[
              { value: ALL, label: "All samples" },
              { value: "has", label: "Has live sample" },
              { value: "pending", label: "Sample pending" },
            ]}
          />

          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              data-testid="clear-filters-btn"
              className="flex items-center gap-1.5 text-xs text-ink-secondary hover:text-ink px-2 py-1 transition-colors"
            >
              <Filter size={12} />
              Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* Table area */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={() => loadLeads()} />
        ) : filtered.length === 0 ? (
          <EmptyState hasLeads={leads.length > 0} onClearFilters={clearFilters} />
        ) : (
          <LeadsTable
            leads={filtered}
            repsById={repsById}
            selectedPlaceId={selectedPlaceId}
            onSelect={setSelectedPlaceId}
            canBulkAssign={canBulkAssign}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            onSelectAll={(placeIds) => {
              // Select all currently filtered leads, or clear if all already selected
              const allSelected = placeIds.every((p) => selectedIds.includes(p));
              if (allSelected) {
                setSelectedIds([]);
              } else {
                setSelectedIds(placeIds);
              }
            }}
          />
        )}
      </div>

      {canBulkAssign && (
        <BulkActionBar
          selectedIds={selectedIds}
          users={users}
          canAssign={canBulkAssign}
          onDone={async () => {
            clearSelection();
            await loadLeads({ silent: true });
          }}
          onClear={clearSelection}
        />
      )}

      {/* Detail slide-over */}
      {selectedPlaceId && (
        <LeadDetailPanel
          placeId={selectedPlaceId}
          onClose={() => setSelectedPlaceId(null)}
          onUpdated={onLeadUpdated}
          users={users}
          currentUser={user}
        />
      )}
    </div>
  );
}

function SelectFilter({ value, onChange, label, options, testId }) {
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <span className="text-ink-muted font-mono uppercase tracking-wider text-[10px]">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="bg-bg border border-line rounded-sm px-2 py-1 text-sm text-ink focus:border-cyan transition-colors max-w-[200px]"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-bg-surface">
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function LeadsTable({ leads, repsById, onSelect, selectedPlaceId, canBulkAssign, selectedIds, onToggleSelected, onSelectAll }) {
  const filteredIds = leads.map((l) => l.place_id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((p) => selectedIds.includes(p));
  const someSelected = filteredIds.some((p) => selectedIds.includes(p));
  return (
    <div className="h-full overflow-y-auto" data-testid="leads-table-scroll">
      <table className="w-full text-sm border-collapse" data-testid="leads-table">
        <thead className="sticky top-0 z-10 bg-bg-surface">
          <tr className="border-b border-line text-left">
            {canBulkAssign && (
              <Th className="w-[3%]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() => onSelectAll(filteredIds)}
                  data-testid="select-all-checkbox"
                  className="h-4 w-4 accent-cyan cursor-pointer"
                  aria-label="Select all filtered leads"
                />
              </Th>
            )}
            <Th className={canBulkAssign ? "w-[29%]" : "w-[32%]"}>Business</Th>
            <Th className="w-[13%]">City</Th>
            <Th className="w-[12%]">Rating</Th>
            <Th className="w-[13%]">Status</Th>
            <Th className="w-[12%]">Assigned</Th>
            <Th className="w-[8%]">Sample</Th>
            <Th className="w-[10%]">Signals</Th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => {
            const isSelected = l.place_id === selectedPlaceId;
            const isChecked = selectedIds.includes(l.place_id);
            const rep = l.assigned_rep_id ? repsById[l.assigned_rep_id] : null;
            return (
              <tr
                key={l.place_id}
                onClick={() => onSelect(l.place_id)}
                data-testid={`lead-row-${l.place_id}`}
                className={`border-b border-line cursor-pointer transition-colors ${
                  isSelected ? "bg-bg-active" : isChecked ? "bg-cyan-muted" : "hover:bg-bg-hover"
                }`}
              >
                {canBulkAssign && (
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onToggleSelected(l.place_id)}
                      data-testid={`select-lead-${l.place_id}`}
                      className="h-4 w-4 accent-cyan cursor-pointer"
                      aria-label={`Select ${l.name}`}
                    />
                  </td>
                )}
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    {isSelected && (
                      <span className="h-4 w-0.5 bg-cyan shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-ink truncate font-medium">{l.name}</div>
                      <div className="text-[11px] text-ink-muted truncate mt-0.5">
                        {l.category}
                        {l.owner && (
                          <>
                            <span className="mx-1.5">·</span>
                            <span>Owner: {l.owner}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-ink-secondary">
                  <div className="truncate">{l.city}</div>
                  <div className="text-[11px] text-ink-muted truncate mt-0.5">{l.metro}</div>
                </td>
                <td className="px-3 py-2.5">
                  <StarRating rating={l.rating} reviews={l.reviews} />
                </td>
                <td className="px-3 py-2.5">
                  <StatusPill status={l.status} />
                </td>
                <td className="px-3 py-2.5 text-ink-secondary">
                  {rep ? (
                    <span className={rep.active ? "text-ink" : "text-ink-muted line-through"}>
                      {rep.name}
                    </span>
                  ) : (
                    <span className="text-ink-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {l.sample_url ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] text-cyan font-mono uppercase tracking-wider"
                      data-testid="sample-live"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-cyan" />
                      Live
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] text-ink-muted font-mono uppercase tracking-wider"
                      data-testid="sample-pending"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-ink-muted" />
                      Pending
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {l.customized && (
                      <span
                        data-testid={`lead-signal-custom-${l.place_id}`}
                        className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border"
                        style={{
                          color: "#22D3C0",
                          borderColor: "rgba(34, 211, 192, 0.4)",
                          backgroundColor: "rgba(34, 211, 192, 0.08)",
                        }}
                        title="Customer submitted customization intake"
                      >
                        Custom
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-ink-muted font-medium ${className}`}
    >
      {children}
    </th>
  );
}

function LoadingState() {
  return (
    <div className="p-6" data-testid="leads-loading">
      <div className="space-y-2">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="h-9 bg-bg-surface border border-line rounded-sm animate-pulse"
            style={{ opacity: 1 - i * 0.07 }}
          />
        ))}
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="p-10" data-testid="leads-error">
      <div
        className="max-w-md border rounded-sm p-4"
        style={{
          backgroundColor: "rgba(239, 68, 68, 0.06)",
          borderColor: "rgba(239, 68, 68, 0.3)",
        }}
      >
        <div className="flex items-start gap-2 mb-2">
          <AlertTriangle size={16} style={{ color: "#F87171" }} className="shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium" style={{ color: "#F87171" }}>
              Couldn&rsquo;t load leads
            </div>
            <div className="text-xs text-ink-secondary mt-1">{message}</div>
          </div>
        </div>
        <button
          onClick={onRetry}
          data-testid="leads-retry-btn"
          className="mt-2 text-xs border border-line text-ink hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function EmptyState({ hasLeads, onClearFilters }) {
  return (
    <div className="p-10" data-testid="leads-empty">
      <div className="border border-line rounded-sm p-6 max-w-md bg-bg-surface">
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-1">
          No results
        </div>
        <div className="text-ink mb-2 text-sm font-medium">
          {hasLeads ? "No leads match the current filters" : "No leads in the system yet"}
        </div>
        <div className="text-[13px] text-ink-secondary mb-4 leading-relaxed">
          {hasLeads
            ? "Try clearing filters — the pipeline moves left to right: new → emailed → called → interested → call_booked → customizing → closed_won."
            : "Once the seed feed syncs (admin action), leads land in the 'new' column. Assign them, then work them through the pipeline."}
        </div>
        {hasLeads && (
          <button
            onClick={onClearFilters}
            data-testid="leads-clear-filters-btn"
            className="text-xs border border-line text-ink hover:bg-bg-hover px-3 py-1.5 rounded-sm transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
